/* eslint-disable @typescript-eslint/dot-notation */
import { capitalize } from 'lodash-es'

import { DEVICE_MODEL, MAPPING_COMMANDS_TYPE } from '~/config/constants'
import { VehicleLastPositionData } from '~/gqlcodegen/fms/types'
import { formatDisplayDate } from '~/utils/date'

const isRentStatusActive = (status?: string) =>
  String(status).toLowerCase() === 'true'

/**
 * @deprecated Transitional fallback, used only when the backend does not yet
 * expose `canDetachGrab`. The backend `GrabDetachTenants` config is the source
 * of truth. Remove once the backend flag is live in all environments.
 */
export const ALLOWED_DETACH_GRAB_TENANT_IDS = [
  'c42967a1-c68a-45f5-90ac-4f7f18203da4',
  '79a40ca1-4c1f-4113-b6bf-43e805c7288e',
  '40fef1d8-d161-4878-93c3-4dd3c511e85e',
  '62624990-7704-4d36-9312-753a3d9919b9',
  '164907e3-eb32-41d1-9d66-8fbd169e77d0',
  'ee74444c-db0f-44eb-abc1-8e5c3b689f3b',
  'd1327426-c948-4be5-b5a2-562fa79b45dd',
  'f65e9647-c844-4eb2-88f2-55337fb55350',
  'd2aebb56-80f2-4f36-a146-4d65691cdd87',
  '2f7dd22c-3321-462d-a01f-36a81cb0e9b2',
  'd2332d39-cfa8-4190-9292-46c1ef1d51df',
  '5f045403-6fe6-47f3-a6b7-bd4bed2f43c6',
]

const constructDetailData = (
  detailVehicleData = {} as VehicleLastPositionData
) => {
  const vehicle = detailVehicleData.vehicle
  const position = detailVehicleData.position

  const vehicleProp: any = vehicle?.properties?.reduce((property, propVal) => {
    const { field, value } = propVal
    return {
      ...property,
      [field.toLowerCase()]: value,
    }
  }, {})

  const positionProp = position?.properties?.reduce((property, propVal) => {
    const { field, value } = propVal
    return {
      ...property,
      [field.toLowerCase()]: value,
    }
  }, {})

  const status = position?.status || 'n/a'
  const latestTime = position?.positioningTime
    ? formatDisplayDate(position.positioningTime, 'DD/MM/YYYY, HH:mm:ss [WIB]')
    : '-'

  const prop = position?.properties || []

  const isCAN = vehicle?.deviceModel === 'TFT100 CAN'
  const powerCANConverted = position?.ignition ? '1' : '0'
  const power = isCAN
    ? powerCANConverted
    : positionProp?.['supersoco.power'] || '0'

  return {
    vehicleName: vehicle?.fleetName || '-',
    plateNo: vehicle?.licensePlate || '-',
    vehicleID: vehicle?.ID || '-',
    vinNO: vehicle?.vinNO || '-',
    tenantID: (vehicle?.tenantID || '').trim(),
    status,
    fleetModelsID: vehicle?.fleetModelsID,
    fleetModels: vehicle?.fleetModels || '',
    fleetTypeID: vehicle?.fleetTypeID,
    fleetTypes: vehicle?.fleetType,
    latestTime,
    latestTimeNoFormat: position?.positioningTime || '',
    make: vehicleProp?.make || 'empty',
    year: vehicleProp?.year || 'empty',
    type: vehicleProp?.type || 'empty',
    fleetType: vehicleProp?.fleet_type || 'empty',
    fleetDescription: vehicleProp?.fleet_description || 'empty',
    battery: positionProp?.['supersoco.battery.capacity'] || '0',
    batterySoh:
      positionProp?.['asset.battery.soh'] ||
      positionProp?.['asset.battery.soh.1'] ||
      0,
    batterySoh2: positionProp?.['asset.battery.soh.2'] || 0,
    assetBatteryID: positionProp?.['asset.battery.id.1'] || 0,
    assetBatteryID2: positionProp?.['asset.battery.id.2'] || 0,
    CANBattery: {
      battery1:
        positionProp?.['asset.battery.soc'] ||
        positionProp?.['asset.battery.soc.1'] ||
        positionProp?.['can6.bms_1_soc'] ||
        positionProp?.['can7.bat_soc'] ||
        '0',
      battery2:
        positionProp?.['asset.battery.soc.2'] ||
        positionProp?.['can7.bms_2_soc'] ||
        '0',
    },
    busBattery:
      positionProp?.['asset.battery.soc'] ||
      positionProp?.['asset.battery.soc.1'] ||
      // positionProp?.['can0.bat_soc'] ||
      // positionProp?.['can7.bat_soc'] ||
      '0',
    busOdometer:
      positionProp?.['odometer'] ||
      // positionProp?.['can1.tot_mileage'] ||
      // positionProp?.['can8.tot_mileage'] ||
      // positionProp?.['can11.odometer'] ||
      positionProp?.['total.odometer'] ||
      '0',
    deviceModel: vehicle?.deviceModel || '-',
    deviceID: vehicle?.deviceID,
    imei: position?.imei || '-',
    isAntiTheftActive: positionProp?.['digital.output.2'],
    isBattery1SecurityGuardActive: isRentStatusActive(
      positionProp?.['can2.bms_1_rent_status']
    ),
    isBattery2SecurityGuardActive: isRentStatusActive(
      positionProp?.['can3.bms_2_rent_status']
    ),
    battery1SecurityGuardSocLimit: positionProp?.['can8.bms_1_chg_soc_limit'],
    battery2SecurityGuardSocLimit: positionProp?.['can9.bms_2_chg_soc_limit'],
    range:
      positionProp?.['odometer'] ||
      position?.mileage ||
      (positionProp?.['supersoco.total.trip.range']
        ? (Number(positionProp?.['supersoco.total.trip.range']) / 1000).toFixed(
            0
          )
        : '') ||
      positionProp?.['total.odometer'] ||
      positionProp?.['can11.odometer'] ||
      positionProp?.['can5.odd'] ||
      '0',
    power,
    lat: position?.latitude ?? null,
    lng: position?.longitude ?? null,
    speed:
      positionProp?.['speed'] ||
      // position?.speed ||
      positionProp?.['can9.speed'] ||
      0,
    direction: position?.course || 0,
    remark: vehicle?.remark || '',
    positionProperties: [...prop].sort((a, b) => (a.field > b.field ? 1 : -1)),
  }
}

export const defaultDetailData = {
  vehicleName: '-',
  plateNo: '-',
  vehicleID: '-',
  vinNO: '-',
  tenantID: '',
  status: 'n/a',
  fleetModelsID: undefined,
  fleetModels: '',
  fleetTypeID: undefined,
  fleetTypes: undefined,
  latestTime: '-',
  latestTimeNoFormat: '',
  make: 'empty',
  year: 'empty',
  type: 'empty',
  fleetType: 'empty',
  fleetDescription: 'empty',
  battery: '0',
  batterySoh: 0,
  batterySoh2: 0,
  assetBatteryID: 0,
  assetBatteryID2: 0,
  CANBattery: {
    battery1: '0',
    battery2: '0',
  },
  busBattery: '0',
  busOdometer: '0',
  deviceModel: '-',
  deviceID: undefined,
  isAntiTheftActive: '',
  isBattery1SecurityGuardActive: false,
  isBattery2SecurityGuardActive: false,
  battery1SecurityGuardSocLimit: undefined,
  battery2SecurityGuardSocLimit: undefined,
  imei: '-',
  range: '0',
  power: '0',
  lat: null,
  lng: null,
  speed: 0,
  direction: 0,
  remark: '',
  positionProperties: [],
}

export type DetailVehicleData = ReturnType<typeof constructDetailData>

const updateVehiclePositionData = (
  positions = { vehicle: {} as any, position: {} as any },
  deviceModel = ''
) => {
  const vehiclePublishedData = positions.vehicle
  const positionPublishedData = positions.position

  const vehiclePublishedProp = vehiclePublishedData?.properties?.reduce(
    (property, propVal) => {
      const { field, value } = propVal
      return {
        ...property,
        [field.toLowerCase()]: value,
      }
    },
    {}
  )

  const positionPublishedProp = positionPublishedData?.properties?.reduce(
    (property, propVal) => {
      const { field, value } = propVal
      return {
        ...property,
        [field.toLowerCase()]: value,
      }
    },
    {}
  )

  const latestTime = positionPublishedData?.positioning_time
    ? formatDisplayDate(
        positionPublishedData.positioning_time,
        'DD/MM/YYYY, HH:mm:ss [WIB]'
      )
    : '-'

  const prop = positionPublishedData?.properties || []

  const isCAN = deviceModel === 'TFT100 CAN'
  const powerCANConverted = positionPublishedData?.ignition ? '1' : '0'
  const power = isCAN
    ? powerCANConverted
    : positionPublishedProp?.['supersoco.power'] || '0'

  return {
    vehicleName: vehiclePublishedData?.fleetName || '-',
    plateNo: vehiclePublishedData?.licensePlate || '-',
    vehicleID: vehiclePublishedData?.ID || '-',
    vinNO: vehiclePublishedData?.vinNo || '-',
    status: capitalize(positionPublishedData?.status) || 'Stop',
    latestTime,
    latestTimeNoFormat: positionPublishedData?.positioning_time || '',
    fleetModels: vehiclePublishedData?.fleetModels || 'empty',
    fleetTypes: vehiclePublishedData?.fleetType,
    fleetType: vehiclePublishedProp?.fleet_type || 'empty',
    fleetDescription: vehiclePublishedProp?.fleet_description || 'empty',
    make: vehiclePublishedProp?.make || 'empty',
    year: vehiclePublishedProp?.year || 'empty',
    type: vehiclePublishedProp?.type || 'empty',
    battery: positionPublishedProp?.['supersoco.battery.capacity'] || '0',
    batterySoh:
      positionPublishedProp?.['asset.battery.soh'] ||
      positionPublishedProp?.['asset.battery.soh.1'] ||
      0,
    batterySoh2: positionPublishedProp?.['asset.battery.soh.2'] || 0,
    assetBatteryID: positionPublishedProp?.['asset.battery.id.1'] || 0,
    assetBatteryID2: positionPublishedProp?.['asset.battery.id.2'] || 0,
    CANBattery: {
      battery1:
        positionPublishedProp?.['asset.battery.soc'] ||
        positionPublishedProp?.['asset.battery.soc.1'] ||
        positionPublishedProp?.['can6.bms_1_soc'] ||
        positionPublishedProp?.['can7.bat_soc'] ||
        '0',
      battery2:
        positionPublishedProp?.['asset.battery.soc.2'] ||
        positionPublishedProp?.['can7.bms_2_soc'] ||
        '0',
    },
    busBattery:
      positionPublishedProp?.['asset.battery.soc'] ||
      positionPublishedProp?.['asset.battery.soc.1'] ||
      // positionPublishedProp?.['can0.bat_soc'] ||
      // positionPublishedProp?.['can7.bat_soc'] ||
      '0',
    busOdometer:
      positionPublishedProp?.['odometer'] ||
      // positionPublishedProp?.['can1.tot_mileage'] ||
      // positionPublishedProp?.['can8.tot_mileage'] ||
      // positionPublishedProp?.['can11.odometer'] ||
      positionPublishedProp?.['total.odometer'] ||
      '0',
    deviceModel,
    deviceID: vehiclePublishedData?.deviceID,
    imei: positionPublishedData?.imei || '-',
    isAntiTheftActive: positionPublishedProp?.['digital.output.2'],
    isBattery1SecurityGuardActive: isRentStatusActive(
      positionPublishedProp?.['can2.bms_1_rent_status']
    ),
    isBattery2SecurityGuardActive: isRentStatusActive(
      positionPublishedProp?.['can3.bms_2_rent_status']
    ),
    battery1SecurityGuardSocLimit:
      positionPublishedProp?.['can8.bms_1_chg_soc_limit'],
    battery2SecurityGuardSocLimit:
      positionPublishedProp?.['can9.bms_2_chg_soc_limit'],
    range:
      positionPublishedProp?.['odometer'] ||
      positionPublishedData?.mileage ||
      (positionPublishedProp?.['supersoco.total.trip.range']
        ? (
            Number(positionPublishedData?.['supersoco.total.trip.range']) / 1000
          ).toFixed(0)
        : '') ||
      positionPublishedProp?.['total.odometer'] ||
      positionPublishedProp?.['can11.odometer'] ||
      positionPublishedProp?.['can5.odd'] ||
      '0',
    power,
    lat: positionPublishedData?.latitude ?? null,
    lng: positionPublishedData?.longitude ?? null,
    speed:
      positionPublishedProp?.['speed'] || positionPublishedData?.speed || 0,
    direction: positionPublishedData?.course || 0,
    remark: vehiclePublishedData?.remark || '',
    positionProperties: [...prop].sort((a, b) => (a.field > b.field ? 1 : -1)),
  }
}

export { constructDetailData, updateVehiclePositionData }
export type VehiclePositionData = ReturnType<typeof updateVehiclePositionData>

export const ON_OFF_COMMANDS = (currentPower, deviceModel) => {
  if (
    deviceModel === DEVICE_MODEL.CAN ||
    deviceModel?.includes(DEVICE_MODEL.CAN)
  ) {
    const command = currentPower
      ? MAPPING_COMMANDS_TYPE.CAN_OFF
      : MAPPING_COMMANDS_TYPE.CAN_ON
    return command
  }
  return currentPower
    ? MAPPING_COMMANDS_TYPE.SUPERSOCO_LOCK
    : MAPPING_COMMANDS_TYPE.SUPERSOCO_POWERON
}
